/** @jsxImportSource @emotion/react */

import { FC, useState } from 'react'
import Typography from '@mui/material/Typography'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import IconButton from '@mui/material/IconButton'
import { ReactComponent as InputIcon } from 'assets/icons/input.svg'
import { findAllQuestion_findAllQuestion_items as Question } from '__generatedTypes__/findAllQuestion'
import { ReactComponent as DnDIcon } from 'assets/icons/dnd-upd.svg'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import Badge from 'components/Badge'
import * as classes from '../../styles'
import { EditTitleForm } from '../EditTitleForm'

export type QuestionPassageBadge = {
  text: string
  color: string
}

type Props = {
  index: number
  expanded: string | null
  question: Question
  sectionBadge?: QuestionPassageBadge | null
  passageBadge?: QuestionPassageBadge | null
  onSuccessfulEdit?: (question: Question, index: number) => void
  onEditClick: (e: React.SyntheticEvent<SVGSVGElement>, question: Question, index: number) => void
  onDeleteClick: (e: React.SyntheticEvent<SVGSVGElement>, question: Question, index: number) => void
}

export const AccordionSummaryContent: FC<Props> = ({
  index,
  expanded,
  question,
  sectionBadge,
  passageBadge,
  onSuccessfulEdit,
  onEditClick,
  onDeleteClick,
}) => {
  const [isEditing, setIsEditing] = useState(false)

  const handleEditTitleButtonClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setIsEditing(true)
  }

  const handleStopEditingTitle = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setIsEditing(false)
  }

  const handleSuccessfulEdit = (question: Question) => {
    setIsEditing(false)
    onSuccessfulEdit?.(question, index)
  }

  return (
    <>
      <div css={[classes.accordionSummaryBlock, isEditing && classes.accordionSummaryEditBlock]}>
        <Typography variant="body3" css={classes.index} data-testid="question-index">
          {index + 1}
        </Typography>

        {isEditing ? (
          <EditTitleForm
            question={question}
            onStopEditingTitle={handleStopEditingTitle}
            onSuccessfulEdit={handleSuccessfulEdit}
          />
        ) : (
          <>
            <Typography variant="body2" css={classes.title} data-testid="question-title">
              {question.title}
            </Typography>
            <IconButton
              onClick={handleEditTitleButtonClick}
              css={classes.iconButton}
              data-testid="question-edit-title-button"
            >
              <InputIcon css={classes.inputIcon} />
            </IconButton>
            {sectionBadge && <Badge css={classes.passageBadge} text={sectionBadge.text} color={sectionBadge.color} />}
            {passageBadge && <Badge css={classes.passageBadge} text={passageBadge.text} color={passageBadge.color} />}
          </>
        )}
      </div>
      <div css={classes.accordionSummaryBlock}>
        {!isEditing && (
          <>
            <EditIcon
              css={[classes.icon, classes.iconEdit]}
              onClick={(e) => onEditClick(e, question, index)}
              data-testid="question-edit-button"
            />
            <CloseRoundedIcon
              css={[classes.icon, classes.iconCross]}
              onClick={(e) => onDeleteClick(e, question, index)}
              data-testid="question-delete-button"
            />
          </>
        )}
        <ExpandMoreRoundedIcon
          css={[classes.iconChevron, expanded === question.id && classes.iconChevronOpen]}
          data-testid="question-expand-icon"
        />
        <DnDIcon />
      </div>
    </>
  )
}
